import React, { memo, useCallback, useEffect, useState } from "react";
import { Table, Button, Tooltip, Modal } from "antd";
import { UndoOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import { getApi, putApi } from "utils/apiCall";

const { confirm } = Modal;

// Deleted staff, with Reactivate. `compact` is the narrow panel shown beside
// the Staff Directory (name, deleted date and action only); `onReactivated`
// lets that page refresh its own list.
const DeletedUsersTab = ({ refreshKey, compact = false, onReactivated }) => {
  const [deletedUsers, setDeletedUsers] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [reactivating, setReactivating] = useState(null);

  const fetchDeletedUsers = useCallback(async () => {
    setTableLoading(true);
    try {
      const { error, result } = await getApi(`/get_all_deleted_users`);

      if (error) {
        // No toast here — the table's empty state covers this case.
        setDeletedUsers([]);
      } else {
        const users = result?.result || [];
        setDeletedUsers(users.map(user => ({
          ...user,
          key: user.user_id || user.id,
          id: user.user_id || user.id,
          full_name: user.first_name && user.last_name
            ? `${user.first_name} ${user.last_name}`.trim()
            : user.first_name || user.last_name || 'N/A',
          email: user.email === 'null' || !user.email ? 'N/A' : user.email,
          mobile_no: user.mobile || 'N/A',
          role: user.role_name || 'N/A',
          organization_name: user.org_name || 'N/A',
          deleted_at: user.last_modified || user.created_date || 'N/A',
        })));
      }
    } catch (err) {
      console.error("Error fetching deleted staff:", err);
      setDeletedUsers([]);
    } finally {
      setTableLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDeletedUsers();
  }, [fetchDeletedUsers, refreshKey]);

  const handleReactivate = useCallback((userId) => {
    confirm({
      title: "Reactivate Staff",
      icon: <ExclamationCircleOutlined />,
      content: "Are you sure you want to reactivate this staff member? They will regain access to the system.",
      okText: "Reactivate",
      okType: "primary",
      cancelText: "Cancel",
      onOk: async () => {
        setReactivating(userId);
        try {
          const { error, result } = await putApi(`/reactivate_deleted_user/${userId}`, {});

          if (error) {
            toast.error(result?.message || "The staff member could not be reactivated. Please try again.");
          } else {
            toast.success("Staff member reactivated successfully.");
            fetchDeletedUsers(); // Refresh the list
            if (onReactivated) onReactivated();
          }
        } catch (err) {
          console.error("Error reactivating staff:", err);
          toast.error("The staff member could not be reactivated. Please try again.");
        } finally {
          setReactivating(null);
        }
      },
    });
  }, [fetchDeletedUsers, onReactivated]);

  const formatDate = (date) => {
    if (!date || date === 'N/A') return 'N/A';
    try {
      // Asepsis apps show dates as DD-MM-YYYY.
      const d = new Date(date);
      if (isNaN(d)) return date;
      const pad = (n) => String(n).padStart(2, "0");
      return compact
        ? `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`
        : `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch {
      return date;
    }
  };

  const actionColumn = {
    title: "Action",
    key: "action",
    render: (_, record) => (
      <Tooltip title="Reactivate Staff">
        <Button
          size="small"
          type="primary"
          style={{
            minWidth: 32,
            fontWeight: 600,
            fontSize: 12,
            background: "#1A8F5C",
            borderColor: "#1A8F5C",
          }}
          onClick={() => handleReactivate(record.id || record.user_id)}
          loading={reactivating === (record.id || record.user_id)}
          icon={<UndoOutlined />}
        >
          {compact ? null : "Reactivate"}
        </Button>
      </Tooltip>
    ),
  };

  const nameColumn = {
    title: "Name",
    dataIndex: "full_name",
    key: "full_name",
    sorter: (a, b) => (a.full_name || '').localeCompare(b.full_name || ''),
  };
  const deletedColumn = {
    title: compact ? "Deleted" : "Deleted At",
    dataIndex: "deleted_at",
    key: "deleted_at",
    render: formatDate,
  };

  const columns = compact
    ? [nameColumn, deletedColumn, actionColumn]
    : [
      nameColumn,
      {
        title: "Username",
        dataIndex: "user_name",
        key: "user_name",
        render: (username) => username || 'N/A',
      },
      {
        title: "Email",
        dataIndex: "email",
        key: "email",
      },
      {
        title: "Mobile",
        dataIndex: "mobile_no",
        key: "mobile_no",
      },
      {
        title: "Group",
        dataIndex: "organization_name",
        key: "organization_name",
      },
      deletedColumn,
      actionColumn,
    ];

  return (
    <div>
      <div style={{ marginBottom: 16, color: "#5B6472", fontSize: 13 }}>
        {compact
          ? "Deleted staff. Reactivate to restore their access."
          : "Staff who have been deleted from the system. You can reactivate them to restore their access."}
      </div>
      <Table
        columns={columns}
        dataSource={deletedUsers}
        rowKey="key"
        size={compact ? "small" : "middle"}
        loading={tableLoading}
        pagination={{
          pageSize: 10,
          showSizeChanger: false,
        }}
        locale={{ emptyText: "No deleted staff found." }}
        style={{ background: "#fff" }}
      />
    </div>
  );
};

export default memo(DeletedUsersTab);
